import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Users, MessageCircle, Settings, Plus, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/layout/AdminLayout";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ courses: 0, users: 0, testimonials: 0, lessons: 0 });
  const { user } = { user: null };

  useEffect(() => {
    document.title = "Admin — Ana Paula Moraes";
    Promise.all([
      api.get("/admin/courses").then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
      api.get("/admin/testimonials").then((r) => r.data),
    ]).then(async ([courses, users, testimonials]) => {
      let lessons = 0;
      for (const c of courses) {
        try {
          const { data } = await api.get(`/admin/courses/${c.id}`);
          lessons += data.modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
        } catch {}
      }
      setStats({ courses: courses.length, users: users.length, testimonials: testimonials.length, lessons });
    });
  }, []);

  const cards = [
    { icon: BookOpen, label: "Cursos", value: stats.courses, to: "/admin/cursos", n: "I" },
    { icon: Sparkles, label: "Aulas publicadas", value: stats.lessons, to: "/admin/cursos", n: "II" },
    { icon: Users, label: "Usuárias", value: stats.users, to: "/admin/usuarios", n: "III" },
    { icon: MessageCircle, label: "Depoimentos", value: stats.testimonials, to: "/admin/depoimentos", n: "IV" },
  ];

  return (
    <AdminLayout>
      <div className="p-8 lg:p-16 max-w-7xl mx-auto">
        <div className="mb-14 fade-in-up">
          <div className="flex items-center gap-3 mb-5">
            <span className="dot-ornament" />
            <span className="eyebrow">Painel administrativo</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight" style={{ color: "var(--text-primary)" }}>
            Visão <span className="headline-italic-accent">geral</span>
          </h1>
          <p className="mt-4 body-lead max-w-xl">Gerencie todo o conteúdo da plataforma com elegância e simplicidade.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {cards.map((c, i) => (
            <Link
              to={c.to}
              key={i}
              className="brand-card p-7 block fade-in-up group"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`stat-card-${i}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(196,143,149,0.12)" }}>
                  <c.icon size={18} style={{ color: "var(--copper)" }} strokeWidth={1.7} />
                </div>
                <span className="font-display italic text-xl" style={{ color: "var(--copper)" }}>{c.n}</span>
              </div>
              <div className="font-serif text-4xl mb-1 tracking-tight" style={{ color: "var(--text-primary)" }}>{c.value}</div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--text-muted)" }}>{c.label}</div>
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <Link to="/admin/cursos/novo" className="brand-card p-8 flex items-center gap-5 group" data-testid="quick-new-course">
            <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-500 group-hover:rotate-90" style={{ background: "var(--rose-gold)" }}>
              <Plus size={22} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="font-serif text-xl md:text-2xl mb-1" style={{ color: "var(--text-primary)" }}>Criar novo curso</div>
              <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Adicione módulos, aulas e materiais de apoio.</div>
            </div>
            <ArrowRight size={18} style={{ color: "var(--copper)" }} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/admin/configuracoes" className="brand-card p-8 flex items-center gap-5 group" data-testid="quick-settings">
            <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-500 group-hover:rotate-90" style={{ background: "rgba(184,115,51,0.12)" }}>
              <Settings size={22} style={{ color: "var(--copper)" }} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="font-serif text-xl md:text-2xl mb-1" style={{ color: "var(--text-primary)" }}>Editar página inicial</div>
              <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Textos, imagens, contatos e identidade visual.</div>
            </div>
            <ArrowRight size={18} style={{ color: "var(--copper)" }} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
